/**
 * useAEODemo Hook
 *
 * Manages state for the AEO Impact Visualization demo feature.
 * Handles session initialization, element selection, and metric calculations.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  DemoSession,
  AEOElement,
  FAQQuestion,
  MetricsSet,
  FAQDistance,
  CompetitorMetrics,
  initDemoSession,
  calculateImpact,
  getPresets,
  PresetData,
} from '@/services/aeoDemoService';

export interface AEODemoState {
  // Session
  session: DemoSession | null;
  isInitializing: boolean;
  initError: string | null;

  // Elements
  elements: AEOElement[];
  selectedElementIds: string[];

  // Metrics
  baselineMetrics: MetricsSet | null;
  enhancedMetrics: MetricsSet | null;
  competitorMetrics: CompetitorMetrics | null;

  // FAQ Questions
  faqQuestions: FAQQuestion[];

  // Calculation state
  isCalculating: boolean;
  calculationTime: number;

  // Presets
  presets: Record<string, PresetData> | null;
  activePreset: string | null;
}

const initialState: AEODemoState = {
  session: null,
  isInitializing: false,
  initError: null,
  elements: [],
  selectedElementIds: [],
  baselineMetrics: null,
  enhancedMetrics: null,
  competitorMetrics: null,
  faqQuestions: [],
  isCalculating: false,
  calculationTime: 0,
  presets: null,
  activePreset: null,
};

/**
 * Approximate metrics based on element deltas
 * Used for instant UI feedback while server calculates actual values
 */
function approximateMetrics(
  baseline: MetricsSet,
  selectedElements: string[]
): MetricsSet {
  // Simple approximation: boost metrics based on number of selected elements
  const boostFactor = selectedElements.length / 13; // 13 total elements
  const boost = (value: number, maxBoost: number = 30) =>
    Math.min(100, value + (100 - value) * boostFactor * (maxBoost / 100));

  const hasFaq = selectedElements.includes('faq');
  const hasSpecs = selectedElements.includes('specifications');
  const hasComparison = selectedElements.includes('comparison');

  return {
    aeo: {
      intent_coverage: boost(baseline.aeo.intent_coverage, 40),
      faq_quality: hasFaq ? boost(baseline.aeo.faq_quality, 80) : baseline.aeo.faq_quality,
      entity_richness: boost(baseline.aeo.entity_richness, 30),
      readability: boost(baseline.aeo.readability, 20),
      conversational_usability: boost(baseline.aeo.conversational_usability, 35),
    },
    seo: {
      schema_coverage: boost(baseline.seo.schema_coverage, 40),
      meta_optimization: boost(baseline.seo.meta_optimization, 15),
      internal_linking: boost(baseline.seo.internal_linking, 20),
      content_depth: boost(baseline.seo.content_depth, 35),
      keyword_coverage: boost(baseline.seo.keyword_coverage, 25),
    },
    ux: {
      ctas: boost(baseline.ux.ctas, 15),
      trust_elements: boost(baseline.ux.trust_elements, 20),
      media_quality: boost(baseline.ux.media_quality, 10),
      comparison_clarity: hasComparison ? boost(baseline.ux.comparison_clarity, 50) : baseline.ux.comparison_clarity,
    },
    scientific: {
      semanticDensity: boost(baseline.scientific.semanticDensity * 100, 40) / 100,
      answerCoverage: boost(baseline.scientific.answerCoverage * 100, 50) / 100,
      citationReadiness: boost(baseline.scientific.citationReadiness * 100, 45) / 100,
      specCompleteness: hasSpecs ? boost(baseline.scientific.specCompleteness * 100, 60) / 100 : baseline.scientific.specCompleteness,
    },
    overall: {
      aeo: boost(baseline.overall.aeo, 35),
      seo: boost(baseline.overall.seo, 25),
      ux: boost(baseline.overall.ux, 20),
      total: boost(baseline.overall.total, 25),
    },
  };
}

export function useAEODemo(productId?: string) {
  const [state, setState] = useState<AEODemoState>(initialState);
  const debounceRef = useRef<ReturnType<typeof debounce> | null>(null);

  /**
   * Initialize demo session for a product
   */
  const initSession = useCallback(async (
    targetProductId: string,
    mode: 'product' | 'standalone_demo' = 'product'
  ) => {
    setState(s => ({ ...s, isInitializing: true, initError: null }));

    try {
      const session = await initDemoSession(targetProductId, mode);

      // Fetch presets
      const presets = await getPresets(session.sessionId);

      setState(s => ({
        ...s,
        session,
        elements: session.aeoElements,
        selectedElementIds: [],
        baselineMetrics: session.baselineMetrics,
        enhancedMetrics: null,
        competitorMetrics: session.competitorMetrics,
        faqQuestions: session.faqQuestions.map(q => ({
          ...q,
          currentDistance: q.originalDistance,
        })),
        presets,
        activePreset: null,
        isInitializing: false,
      }));

    } catch (error) {
      setState(s => ({
        ...s,
        isInitializing: false,
        initError: error instanceof Error ? error.message : 'Failed to initialize session',
      }));
    }
  }, []);

  /**
   * Toggle an AEO element on/off
   */
  const toggleElement = useCallback((elementId: string) => {
    setState(s => {
      const newSelected = s.selectedElementIds.includes(elementId)
        ? s.selectedElementIds.filter(id => id !== elementId)
        : [...s.selectedElementIds, elementId];

      // Optimistic update with approximation
      const approximateEnhanced = s.baselineMetrics && newSelected.length > 0
        ? approximateMetrics(s.baselineMetrics, newSelected)
        : null;

      return {
        ...s,
        selectedElementIds: newSelected,
        enhancedMetrics: approximateEnhanced,
        isCalculating: newSelected.length > 0,
        activePreset: null, // Clear preset when manually toggling
      };
    });

    // Debounce server calculation
    if (debounceRef.current) {
      debounceRef.current.cancel();
    }

    debounceRef.current = debounce(async () => {
      const currentState = state;
      if (!currentState.session) return;

      const newSelected = currentState.selectedElementIds.includes(elementId)
        ? currentState.selectedElementIds.filter(id => id !== elementId)
        : [...currentState.selectedElementIds, elementId];

      if (newSelected.length === 0) {
        setState(s => ({
          ...s,
          enhancedMetrics: null,
          isCalculating: false,
          faqQuestions: s.faqQuestions.map(q => ({
            ...q,
            currentDistance: q.originalDistance,
          })),
        }));
        return;
      }

      try {
        const result = await calculateImpact(currentState.session.sessionId, newSelected);

        setState(s => ({
          ...s,
          enhancedMetrics: result.enhancedMetrics,
          faqQuestions: s.faqQuestions.map(q => {
            const distance = result.faqDistances.find(d => d.id === q.id);
            return {
              ...q,
              currentDistance: distance?.newDistance ?? q.originalDistance,
            };
          }),
          isCalculating: false,
          calculationTime: result.calculationTime,
        }));

      } catch (error) {
        console.error('[useAEODemo] Calculate error:', error);
        setState(s => ({ ...s, isCalculating: false }));
      }
    }, 500);

    debounceRef.current();
  }, [state.session, state.selectedElementIds]);

  /**
   * Select a preset configuration
   */
  const selectPreset = useCallback(async (presetName: string) => {
    const presetData = state.presets?.[presetName];
    if (!presetData) return;

    setState(s => ({
      ...s,
      selectedElementIds: presetData.elements,
      enhancedMetrics: presetData.metrics,
      faqQuestions: s.faqQuestions.map(q => {
        const distance = presetData.distances.find(d => d.id === q.id);
        return {
          ...q,
          currentDistance: distance?.newDistance ?? q.originalDistance,
        };
      }),
      activePreset: presetName,
      isCalculating: false,
    }));
  }, [state.presets]);

  /**
   * Clear all selections
   */
  const clearSelections = useCallback(() => {
    if (debounceRef.current) {
      debounceRef.current.cancel();
    }

    setState(s => ({
      ...s,
      selectedElementIds: [],
      enhancedMetrics: null,
      activePreset: null,
      isCalculating: false,
      faqQuestions: s.faqQuestions.map(q => ({
        ...q,
        currentDistance: q.originalDistance,
      })),
    }));
  }, []);

  /**
   * Force recalculate with server (bypass approximations)
   */
  const forceRecalculate = useCallback(async () => {
    if (!state.session || state.selectedElementIds.length === 0) return;

    setState(s => ({ ...s, isCalculating: true }));

    try {
      const result = await calculateImpact(
        state.session.sessionId,
        state.selectedElementIds,
        false // Don't use quick mode
      );

      setState(s => ({
        ...s,
        enhancedMetrics: result.enhancedMetrics,
        faqQuestions: s.faqQuestions.map(q => {
          const distance = result.faqDistances.find(d => d.id === q.id);
          return {
            ...q,
            currentDistance: distance?.newDistance ?? q.originalDistance,
          };
        }),
        isCalculating: false,
        calculationTime: result.calculationTime,
      }));

    } catch (error) {
      console.error('[useAEODemo] Force recalculate error:', error);
      setState(s => ({ ...s, isCalculating: false }));
    }
  }, [state.session, state.selectedElementIds]);

  // Auto-init if productId provided
  useEffect(() => {
    if (productId) {
      initSession(productId, 'product');
    }
  }, [productId, initSession]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        debounceRef.current.cancel();
      }
    };
  }, []);

  return {
    ...state,
    initSession,
    toggleElement,
    selectPreset,
    clearSelections,
    forceRecalculate,
  };
}

export default useAEODemo;
